import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  InputAdornment,
  useTheme,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  Delete,
  PictureAsPdf,
  Add as AddIcon,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import {
  BusinessCourse,
  BusinessCourseMedia,
  CourseLevel,
  CourseLanguage,
  CourseType,
  CourseAccessType,
  CreateBusinessCourseRequest,
  UpdateBusinessCourseRequest,
} from '@/constants/types';
import { businessCourseService } from '@/services/business/businessCourseService';
import { uploadService } from '@/services/upload/uploadService';
import { SlateRichTextEditor } from '@/components/dashboard/SlateRichTextEditor';

interface AddEditBusinessCourseModalProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  editCourse?: BusinessCourse | null;
  onSuccess: () => void;
}

const LEVELS: CourseLevel[] = ['Beginner', 'Intermediate', 'Advanced', 'All'];
const LANGUAGES: CourseLanguage[] = ['English', 'Tamil', 'Bilingual'];
const COURSE_TYPES: CourseType[] = ['Free', 'Paid'];
const ACCESS_TYPES: CourseAccessType[] = ['Lifetime', 'Limited', 'Subscription'];

const MODULES_REGEX = /^\d*$/;

const HASHTAG_MAX_LENGTH = 10;
const HASHTAG_MAX_COUNT = 5;

const THUMBNAIL_RECOMMENDED = { width: 1280, height: 720 };

const readImageDimensions = (url: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to read image'));
    img.src = url;
  });

const extractPlainTextFromSlate = (value: string): string => {
  if (!value) return '';
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return value;
    const collect = (nodes: any[]): string =>
      nodes
        .map((n) => {
          if (typeof n?.text === 'string') return n.text;
          if (Array.isArray(n?.children)) return collect(n.children);
          return '';
        })
        .join(' ');
    return collect(parsed).replace(/\s+/g, ' ').trim();
  } catch {
    return value;
  }
};

const wordCount = (text: string) => (text ? text.split(/\s+/).filter(Boolean).length : 0);

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 2 : 1)} MB`;
};

const validationSchema = Yup.object({
  courseTitle: Yup.string().max(200, 'Title must be at most 200 characters').required('Title is required'),
  level: Yup.string().oneOf(LEVELS).required('Level is required'),
  language: Yup.string().oneOf(LANGUAGES),
  description: Yup.string()
    .test('not-empty', 'Description is required', (value) => extractPlainTextFromSlate(value || '').length > 0)
    .test(
      'word-count',
      'Description must be at most 200 words',
      (value) => wordCount(extractPlainTextFromSlate(value || '')) <= 200
    ),
  duration: Yup.string().max(100, 'Duration must be at most 100 characters'),
  numberOfModules: Yup.string()
    .matches(MODULES_REGEX, 'Modules must be a number')
    .nullable(),
  courseType: Yup.string().oneOf(COURSE_TYPES).required('Course type is required'),
  price: Yup.number().min(0, 'Price must be positive').nullable(),
  discount: Yup.number().min(0, 'Discount must be 0-100').max(100, 'Discount must be 0-100').nullable(),
  accessType: Yup.string().oneOf(ACCESS_TYPES),
});

export const AddEditBusinessCourseModal: React.FC<AddEditBusinessCourseModalProps> = ({
  open,
  onClose,
  businessId,
  editCourse,
  onSuccess,
}) => {
  const theme = useTheme();
  const isEditing = !!editCourse;

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Existing media (kept until user removes/replaces)
  const [existingThumbnail, setExistingThumbnail] = useState<BusinessCourseMedia | null>(null);
  const [existingBrochure, setExistingBrochure] = useState<BusinessCourseMedia | null>(null);

  // Newly selected files (uploaded only on submit)
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [thumbnailDimensions, setThumbnailDimensions] = useState<{ width: number; height: number } | null>(null);
  const [brochureFile, setBrochureFile] = useState<File | null>(null);

  const objectUrlRef = useRef<string | null>(null);

  const [hashTagInput, setHashTagInput] = useState('');
  const [hashTags, setHashTags] = useState<string[]>([]);

  const formik = useFormik({
    initialValues: {
      courseTitle: editCourse?.courseTitle || '',
      level: (editCourse?.level || 'Beginner') as CourseLevel,
      language: (editCourse?.language || 'English') as CourseLanguage,
      description: editCourse?.description || '',
      duration: editCourse?.duration || '',
      numberOfModules: editCourse?.numberOfModules?.toString() || '',
      certificationAvailable: editCourse?.certificationAvailable || false,
      courseType: (editCourse?.courseType || 'Free') as CourseType,
      price: editCourse?.price?.toString() || '',
      discount: editCourse?.discount?.toString() || '',
      accessType: (editCourse?.accessType || 'Lifetime') as CourseAccessType,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);

        let thumbnailMedia: BusinessCourseMedia | undefined = existingThumbnail || undefined;
        if (thumbnailFile) {
          const response = await uploadService.uploadSingle(thumbnailFile, { folder: 'business-courses' });
          thumbnailMedia = {
            mediaFileId: response.file.id,
            url: response.file.url,
            fileName: response.file.fileName,
            contentType: thumbnailFile.type,
            size: thumbnailFile.size,
          };
        }

        let brochureMedia: BusinessCourseMedia | undefined = existingBrochure || undefined;
        if (brochureFile) {
          const response = await uploadService.uploadSingle(brochureFile, { folder: 'course-brochures' });
          brochureMedia = {
            mediaFileId: response.file.id,
            url: response.file.url,
            fileName: response.file.fileName,
            contentType: 'application/pdf',
            size: brochureFile.size,
          };
        }

        const payload: CreateBusinessCourseRequest | UpdateBusinessCourseRequest = {
          courseTitle: values.courseTitle,
          level: values.level,
          language: values.language,
          description: values.description,
          duration: values.duration || undefined,
          numberOfModules: values.numberOfModules ? Number(values.numberOfModules) : undefined,
          certificationAvailable: values.certificationAvailable,
          hashTags: hashTags.length > 0 ? hashTags : undefined,
          courseType: values.courseType,
          price: values.price ? Number(values.price) : 0,
          discount: values.discount ? Number(values.discount) : 0,
          accessType: values.accessType,
          courseThumbnail: thumbnailMedia,
          brochure: brochureMedia,
        };

        if (isEditing && editCourse) {
          await businessCourseService.updateBusinessCourse(editCourse._id, payload);
          toast.success('Course updated successfully');
        } else {
          await businessCourseService.createBusinessCourse({
            ...payload,
            businessId,
          } as CreateBusinessCourseRequest);
          toast.success('Course created successfully');
        }

        onSuccess();
        handleClose();
      } catch (err: any) {
        toast.error(err?.message || 'Failed to save course');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (editCourse) {
      setExistingThumbnail(editCourse.courseThumbnail || null);
      setExistingBrochure(editCourse.brochure || null);
      setHashTags(editCourse.hashTags || []);
    } else {
      setExistingThumbnail(null);
      setExistingBrochure(null);
      setHashTags([]);
    }
    setThumbnailFile(null);
    setBrochureFile(null);
    setThumbnailPreview(null);
    setThumbnailDimensions(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, [editCourse, open]);

  // Read dimensions for the existing thumbnail when editing
  useEffect(() => {
    let cancelled = false;
    const url = existingThumbnail?.url;
    if (!url || thumbnailFile) return;
    readImageDimensions(url)
      .then((dim) => { if (!cancelled) setThumbnailDimensions(dim); })
      .catch(() => { if (!cancelled) setThumbnailDimensions(null); });
    return () => { cancelled = true; };
  }, [existingThumbnail?.url, thumbnailFile]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const handleClose = () => {
    formik.resetForm();
    setExistingThumbnail(null);
    setExistingBrochure(null);
    setThumbnailFile(null);
    setBrochureFile(null);
    setThumbnailPreview(null);
    setThumbnailDimensions(null);
    setHashTags([]);
    setHashTagInput('');
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    onClose();
  };

  const handleThumbnailSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const previewUrl = URL.createObjectURL(file);
    objectUrlRef.current = previewUrl;
    setThumbnailFile(file);
    setThumbnailPreview(previewUrl);
    setExistingThumbnail(null);
    setThumbnailDimensions(null);

    try {
      const dim = await readImageDimensions(previewUrl);
      setThumbnailDimensions(dim);
    } catch {
      setThumbnailDimensions(null);
    }
  };

  const handleBrochureSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed for brochure');
      return;
    }

    setBrochureFile(file);
    setExistingBrochure(null);
  };

  const handleAddHashTag = () => {
    const tag = hashTagInput.trim().replace(/^#/, '');
    if (!tag) return;
    if (tag.length > HASHTAG_MAX_LENGTH) {
      toast.error(`Hashtag must be at most ${HASHTAG_MAX_LENGTH} characters`);
      return;
    }
    if (hashTags.length >= HASHTAG_MAX_COUNT) {
      toast.error(`You can add up to ${HASHTAG_MAX_COUNT} hashtags`);
      return;
    }
    if (hashTags.includes(tag)) {
      setHashTagInput('');
      return;
    }
    setHashTags([...hashTags, tag]);
    setHashTagInput('');
  };

  const handleRemoveHashTag = (tag: string) => {
    setHashTags(hashTags.filter((t) => t !== tag));
  };

  const removeThumbnail = () => {
    setExistingThumbnail(null);
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setThumbnailDimensions(null);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  const removeBrochure = () => {
    setExistingBrochure(null);
    setBrochureFile(null);
  };

  const handleModulesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === '' || MODULES_REGEX.test(v)) {
      formik.setFieldValue('numberOfModules', v);
    }
  };

  const handleDescriptionChange = useCallback((v: string) => {
    formik.setFieldValue('description', v);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDescriptionBlur = useCallback(() => {
    formik.setFieldTouched('description', true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const thumbnailUrl = thumbnailPreview || existingThumbnail?.url || null;
  const brochureName = brochureFile?.name || existingBrochure?.fileName || null;
  const brochureSize = brochureFile?.size ?? existingBrochure?.size;
  const brochureSizeLabel = formatFileSize(brochureSize);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableAutoFocus>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          {isEditing ? 'Edit Course' : 'Add Course'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          {/* Course Title */}
          <TextField
            fullWidth
            required
            label="Course Title"
            name="courseTitle"
            value={formik.values.courseTitle}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.courseTitle && Boolean(formik.errors.courseTitle)}
            helperText={
              (formik.touched.courseTitle && formik.errors.courseTitle) ||
              `${formik.values.courseTitle.length}/200 characters`
            }
            sx={{ mb: 2 }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />

          {/* Level & Language */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              select
              fullWidth
              required
              label="Level"
              name="level"
              value={formik.values.level}
              onChange={formik.handleChange}
              error={formik.touched.level && Boolean(formik.errors.level)}
              helperText={formik.touched.level && formik.errors.level}
            >
              {LEVELS.map((lvl) => (
                <MenuItem key={lvl} value={lvl}>
                  {lvl}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              required
              label="Language"
              name="language"
              value={formik.values.language}
              onChange={formik.handleChange}
            >
              {LANGUAGES.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Description (rich text) */}
          <Box sx={{ mb: 2 }}>
            <SlateRichTextEditor
              title="Description"
              value={formik.values.description}
              onChange={handleDescriptionChange}
              onBlur={handleDescriptionBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={
                (formik.touched.description && (formik.errors.description as string)) ||
                `${wordCount(extractPlainTextFromSlate(formik.values.description))}/200 words`
              }
              placeholder="Describe the course, learning outcomes, prerequisites..."
              maxLength={2000}
            />
          </Box>

          {/* Duration & Modules */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Duration"
              name="duration"
              placeholder="e.g., 8 weeks"
              value={formik.values.duration}
              onChange={formik.handleChange}
              error={formik.touched.duration && Boolean(formik.errors.duration)}
              helperText={formik.touched.duration && formik.errors.duration}
              slotProps={{ htmlInput: { maxLength: 100 } }}
            />
            <TextField
              fullWidth
              label="Number of Modules"
              name="numberOfModules"
              value={formik.values.numberOfModules}
              onChange={handleModulesChange}
              onBlur={formik.handleBlur}
              error={formik.touched.numberOfModules && Boolean(formik.errors.numberOfModules)}
              helperText={formik.touched.numberOfModules && formik.errors.numberOfModules}
              slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '\\d*' } }}
            />
          </Box>

          {/* Certification */}
          <FormControlLabel
            control={
              <Switch
                name="certificationAvailable"
                checked={formik.values.certificationAvailable}
                onChange={formik.handleChange}
              />
            }
            label="Certification Available"
            sx={{ mb: 2 }}
          />

          {/* Course Type / Access */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              select
              fullWidth
              required
              label="Course Type"
              name="courseType"
              value={formik.values.courseType}
              onChange={formik.handleChange}
              error={formik.touched.courseType && Boolean(formik.errors.courseType)}
              helperText={formik.touched.courseType && formik.errors.courseType}
            >
              {COURSE_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Access Type"
              name="accessType"
              value={formik.values.accessType}
              onChange={formik.handleChange}
            >
              {ACCESS_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Price & Discount (only for Paid) */}
          {formik.values.courseType === 'Paid' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                required
                label="Price"
                name="price"
                type="number"
                value={formik.values.price}
                onChange={formik.handleChange}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={formik.touched.price && formik.errors.price}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">&#8377;</InputAdornment>,
                  },
                }}
              />
              <TextField
                fullWidth
                label="Discount %"
                name="discount"
                type="number"
                value={formik.values.discount}
                onChange={formik.handleChange}
                error={formik.touched.discount && Boolean(formik.errors.discount)}
                helperText={formik.touched.discount && formik.errors.discount}
              />
            </Box>
          )}

          {/* Hashtags */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2">Hashtags</Typography>
              <Typography variant="caption" color="text.secondary">
                {hashTags.length}/{HASHTAG_MAX_COUNT} · max {HASHTAG_MAX_LENGTH} chars each
              </Typography>
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder={
                hashTags.length >= HASHTAG_MAX_COUNT
                  ? `Limit reached (${HASHTAG_MAX_COUNT} hashtags)`
                  : 'Add a hashtag and press Enter'
              }
              value={hashTagInput}
              disabled={hashTags.length >= HASHTAG_MAX_COUNT}
              onChange={(e) => setHashTagInput(e.target.value.replace(/^#/, '').slice(0, HASHTAG_MAX_LENGTH))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddHashTag();
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleAddHashTag}
                        disabled={!hashTagInput.trim() || hashTags.length >= HASHTAG_MAX_COUNT}
                        edge="end"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                htmlInput: { maxLength: HASHTAG_MAX_LENGTH },
              }}
              sx={{ mb: 1 }}
            />
            {hashTags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {hashTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={`#${tag}`}
                    size="small"
                    onDelete={() => handleRemoveHashTag(tag)}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Course Thumbnail */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2">Course Thumbnail</Typography>
              <Typography variant="caption" color="text.secondary">
                Recommended: {THUMBNAIL_RECOMMENDED.width}×{THUMBNAIL_RECOMMENDED.height} (16:9)
              </Typography>
            </Box>
            {thumbnailUrl ? (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '16 / 9',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                }}
              >
                <Box
                  component="img"
                  src={thumbnailUrl}
                  alt="Course Thumbnail"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <IconButton
                  onClick={removeThumbnail}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
                {thumbnailDimensions && (
                  <Chip
                    label={`${thumbnailDimensions.width}×${thumbnailDimensions.height}`}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: 'rgba(0,0,0,0.65)',
                      color: 'white',
                    }}
                  />
                )}
                {thumbnailFile && (
                  <Chip
                    label="Will upload on save"
                    size="small"
                    color="info"
                    sx={{ position: 'absolute', bottom: 8, left: 8, height: 20, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            ) : (
              <Box
                component="label"
                sx={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 0.75,
                  px: 2,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CloudUpload sx={{ fontSize: 26 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Click to upload thumbnail
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PNG, JPG · 16:9 · {THUMBNAIL_RECOMMENDED.width}×{THUMBNAIL_RECOMMENDED.height} recommended
                </Typography>
                <input type="file" hidden accept="image/*" onChange={handleThumbnailSelect} />
              </Box>
            )}
          </Box>

          {/* Brochure (PDF) */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Brochure (PDF only)
            </Typography>
            {brochureName ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <PictureAsPdf color="error" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {brochureName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                      {brochureSizeLabel && (
                        <Typography variant="caption" color="text.secondary">
                          {brochureSizeLabel}
                        </Typography>
                      )}
                      {brochureFile && (
                        <Typography variant="caption" color="info.main">
                          · Will upload on save
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={removeBrochure} size="small" color="error">
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                component="label"
                sx={{
                  width: '100%',
                  py: 2.5,
                  px: 2,
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PictureAsPdf sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Click to upload brochure
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF only · max 10 MB
                  </Typography>
                </Box>
                <input type="file" hidden accept="application/pdf" onChange={handleBrochureSelect} />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !formik.isValid}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
